import { Bell, Check } from "lucide-react";
import type { RefObject } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/components/ui/utils";
import { NotificationIcon } from "@/features/notifications/components/NotificationIcon";
import { notificationTarget } from "@/features/notifications/notification-links";
import { useNotifications } from "@/features/notifications/notifications-context";
import { formatBadgeCount, summarizeActivity } from "@/features/notifications/notifications-format";
import type { AppNotification } from "@/features/notifications/types";
import { formatRelative } from "@/lib/format";

const PREVIEW_LIMIT = 5;

interface NotificationBellTriggerProps {
  badgeLabel: string;
  hasUnread: boolean;
  onToggle: () => void;
  open: boolean;
  panelId: string;
  triggerRef: RefObject<HTMLButtonElement | null>;
  unreadCount: number;
}

function NotificationBellTrigger({
  badgeLabel,
  hasUnread,
  onToggle,
  open,
  panelId,
  triggerRef,
  unreadCount,
}: NotificationBellTriggerProps) {
  const ariaLabel = hasUnread
    ? `Notifications, ${unreadCount} unread`
    : "Notifications, none unread";

  return (
    <Button
      aria-controls={open ? panelId : undefined}
      aria-expanded={open}
      aria-haspopup="dialog"
      aria-label={ariaLabel}
      className="relative size-[44px] rounded-full text-muted-foreground sm:size-9"
      onClick={onToggle}
      ref={triggerRef}
      size="icon"
      type="button"
      variant="ghost"
    >
      <Bell aria-hidden="true" className="size-[18px]" />
      {hasUnread && (
        <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 font-medium text-[10px] text-destructive-foreground tabular-nums ring-2 ring-background">
          {badgeLabel}
        </span>
      )}
    </Button>
  );
}

function NotificationPreviewList({
  isUnread,
  onClose,
  onOpen,
  preview,
}: {
  isUnread: (notification: AppNotification) => boolean;
  onClose: () => void;
  /** Marks a notification read when its row is opened. */
  onOpen: (id: string) => void;
  preview: AppNotification[];
}) {
  if (preview.length === 0) {
    return (
      <li className="px-4 py-8 text-center text-muted-foreground text-sm">
        You&rsquo;re all caught up.
      </li>
    );
  }

  return preview.map((notification) => (
    // The separator sits on the `li`, not on the row: a `last:` variant carries
    // `:last-child` specificity, and the row is its list item's only child, so
    // `last:border-b-0` on the row would win over `border-b` on every row.
    <li className="border-border/60 border-b last:border-b-0" key={notification.id}>
      <Link
        // dv-disable-next-line outline-none-requires-offset -- inset ring: an offset ring would clip against the popover list edge
        className="flex min-h-[44px] gap-3 px-4 py-3 hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
        // A middle-click opens a new tab without firing onClick; mark it read too.
        onAuxClick={(event) => {
          if (event.button === 1) {
            onOpen(notification.id);
          }
        }}
        onClick={() => {
          onOpen(notification.id);
          onClose();
        }}
        to={notificationTarget(notification)?.to ?? "/notifications"}
      >
        <NotificationIcon category={notification.category} className="size-9" />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <p
              className={cn(
                "truncate text-sm",
                isUnread(notification)
                  ? "font-semibold text-foreground"
                  : "font-medium text-foreground/90",
              )}
            >
              {notification.title}
            </p>
            <time
              className="shrink-0 text-[11px] text-muted-foreground tabular-nums"
              dateTime={notification.createdAt}
            >
              {formatRelative(notification.createdAt)}
            </time>
          </div>
          <p className="line-clamp-1 text-muted-foreground text-xs">
            {summarizeActivity(notification.action, notification.metadata)}
          </p>
        </div>
      </Link>
    </li>
  ));
}

interface NotificationBellPanelProps {
  hasUnread: boolean;
  isUnread: (notification: AppNotification) => boolean;
  markAllRead: () => void;
  onClose: () => void;
  onOpen: (id: string) => void;
  panelId: string;
  panelRef: RefObject<HTMLDivElement | null>;
  preview: AppNotification[];
  unreadCount: number;
}

function NotificationBellPanel({
  hasUnread,
  isUnread,
  markAllRead,
  onClose,
  onOpen,
  panelId,
  panelRef,
  preview,
  unreadCount,
}: NotificationBellPanelProps) {
  return (
    <Card
      aria-label="Notifications"
      className="absolute right-0 z-50 mt-2 w-[min(22rem,calc(100vw-1.5rem))] origin-top-right gap-0 overflow-hidden rounded-xl border-border bg-popover text-popover-foreground shadow-lg"
      id={panelId}
      ref={panelRef}
      role="dialog"
      tabIndex={-1}
    >
      <div className="flex items-center justify-between gap-2 border-border border-b px-4 py-3">
        <p className="font-medium text-sm">Notifications</p>
        {hasUnread && (
          <span className="rounded-full bg-accent px-2 py-0.5 font-medium text-[11px] text-accent-foreground">
            {unreadCount} new
          </span>
        )}
      </div>

      <ul className="max-h-80 overflow-y-auto">
        <NotificationPreviewList
          isUnread={isUnread}
          onClose={onClose}
          onOpen={onOpen}
          preview={preview}
        />
      </ul>

      <div className="flex items-center justify-between gap-2 border-border border-t p-2">
        <Button
          asChild
          className="text-primary hover:text-primary"
          size="mobileTouchSm"
          variant="ghost"
        >
          <Link onClick={onClose} to="/notifications">
            View all
          </Link>
        </Button>
        {hasUnread && (
          <Button
            className="text-muted-foreground"
            onClick={markAllRead}
            size="mobileTouchSm"
            type="button"
            variant="ghost"
          >
            <Check className="size-4" />
            Mark all read
          </Button>
        )}
      </div>
    </Card>
  );
}

/**
 * Accessible notification bell with an unread badge and a preview dropdown.
 * Reads from the shared notifications store; built with Tailwind utilities only.
 */
export function NotificationBell({ className }: { className?: string }) {
  const { notifications, unreadCount, isUnread, markAllRead, markOneRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const hasUnread = unreadCount > 0;
  const badgeLabel = formatBadgeCount(unreadCount);
  const preview = notifications.slice(0, PREVIEW_LIMIT);

  useEffect(() => {
    if (!open) {
      return;
    }

    panelRef.current?.focus();

    // `pointerdown`, not `mousedown`: touch platforms only synthesize mouse
    // events for targets they consider clickable, so a tap on the page behind
    // the panel could leave it open on a phone — where Escape needs a hardware
    // keyboard and the panel carries no close button.
    function handlePointerDown(event: PointerEvent) {
      const target = event.target;
      if (target instanceof Node && !containerRef.current?.contains(target)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div className={cn("relative", className)} ref={containerRef}>
      <NotificationBellTrigger
        badgeLabel={badgeLabel}
        hasUnread={hasUnread}
        onToggle={() => setOpen((previous) => !previous)}
        open={open}
        panelId={panelId}
        triggerRef={triggerRef}
        unreadCount={unreadCount}
      />

      {open && (
        <NotificationBellPanel
          hasUnread={hasUnread}
          isUnread={isUnread}
          markAllRead={markAllRead}
          onClose={() => setOpen(false)}
          onOpen={markOneRead}
          panelId={panelId}
          panelRef={panelRef}
          preview={preview}
          unreadCount={unreadCount}
        />
      )}
    </div>
  );
}
